import * as React from "react";
import cx from "classnames";

import { DatasheetItem } from "@modules/datasheets/components/datasheet-item";
import { DatasheetItemLabel } from "@modules/datasheets/components/datasheet-item-label";
import { LookupTableFieldProps, useLookupTableFieldController } from "./lookup-table-field.controller";
import { getHeadingAlignmentStyle, getHeadingPositionOffset } from "../field-heading.utils";
import { Flex } from "@common/components/Flex";
import { Hint } from "@common/components/hint";
import Dropdown, { components } from "@common/containers/fields/Dropdown";

import { FieldContainer } from "../field-container";
import { useFieldContentStyle } from '../content-style.utils';
import { DatasheetComponentTextStyle } from "@common/dto/datasheet/component-settings/text-style";

import { ControlProps } from "react-select/lib/components/Control";
import { MenuListComponentProps } from "react-select/lib/components/Menu";
import { Button } from "@common/components/Button";
import { ClickAwayListener } from "@common/components/ClickAwayListener";

import "./lookup-table-field.styles.scss";
import { useForceDisabled } from '../../context';

export const LookupTableField: React.FC<LookupTableFieldProps> = React.memo((props) => {
  const { datasheetElement } = props;

  const {
    headingPlacedHorizontally,
    headingReference,
    headingSize,
    headingPosition,
    normalizeDropdownOptions,
    menuOpened,
    columns,
    selectedItem,
    handleButtonClick,
    handleCloseMenu,
    handleLookupTableFieldValueChange,
    DROPDOWN_PLACEHOLDER,
    containerRef,
    handleTabNavigation,
    isEditable,
  } = useLookupTableFieldController(props);
  const forceDisabled = useForceDisabled();
  const showBorder = datasheetElement.original.showborder !== false;

  const { contentColor, contentAlign, contentFontFamily, contentFontSize, contentTextStyles } = useFieldContentStyle(datasheetElement.original);

  const fieldWidth = datasheetElement.original.width;
  const fieldHeight = datasheetElement.original.height;

  const headingAlignmentStyle = getHeadingAlignmentStyle({
    headingPosition,
    headAlign: datasheetElement.original.headalign,
    fieldWidth,
  });

  const headingPositionOffset = getHeadingPositionOffset({
    headingPosition,
    headAlign: datasheetElement.original.headalign,
    headingSize,
    fieldWidth,
    fieldHeight,
  });

  const labelComponent = (
    <DatasheetItemLabel
      reference={headingReference}
      inline={headingPlacedHorizontally}
      withImg={datasheetElement.original.showfieldtype}
      withText={datasheetElement.original.headdisplay}
      text={datasheetElement.original.headtext}
      img={datasheetElement.original.field.imageURL}
      textCustomization={{
        fontSize: datasheetElement.original.headfontsize,
        fontFamily: datasheetElement.original.headfont,
        color: datasheetElement.original.headcolor,
        align: datasheetElement.original.headalign,
        fontStyles: datasheetElement.original.headstyle,
      }}
      absolutePosition={true}
      alignmentStyle={headingAlignmentStyle}
      positionOffset={headingPositionOffset}
    />
  );

  const fieldIsEmpty = (() => { const v = (datasheetElement.original as any).field?.name ?? (datasheetElement.original as any).field?.value; return v == null || String(v).trim() === ''; })();

  const getMenuStyles = React.useCallback(
    (css: React.CSSProperties) => ({
      ...css,
      width: undefined,
    }),
    []
  );

  const getMenuPortalStyles = React.useCallback((css: React.CSSProperties) => ({ ...css, zIndex: 9999 }), []);

  const getMenuListComponent = (props: MenuListComponentProps<{}>) => {
    return (
      <components.MenuList {...props} className="DatasheetsResultLookupTableField__dropdown__menu">
        <div className="DatasheetsResultLookupTableField__dropdown__menu__table">
          <Flex className="DatasheetsResultLookupTableField__dropdown__menu__table__header">
            {columns.map((column) => (
              <Flex
                alignItems="center"
                className="DatasheetsResultLookupTableField__dropdown__menu__table__header__row"
                style={{
                  width: `${100 / columns.length}%`,
                }}
                key={column.accessorKey}
              >
                {column.header}
              </Flex>
            ))}
          </Flex>
          {normalizeDropdownOptions.map((option) => (
            <Flex
              key={option.value}
              width="100%"
              className={cx("DatasheetsResultLookupTableField__dropdown__menu__table__body__row", {
                "DatasheetsResultLookupTableField__dropdown__menu__table__body__row--selected": 
                  selectedItem?.value === option.value,
              })}
              onClick={() => handleLookupTableFieldValueChange(option.value)}
            >
              {columns.map((column) => (
                <Flex
                  key={column.accessorKey}
                  alignItems="center"
                  className="DatasheetsResultLookupTableField__dropdown__menu__table__body__row__column"
                  style={{
                    width: `${100 / columns.length}%`,
                  }}
                >
                  {option[column.accessorKey]}
                </Flex>
              ))}
            </Flex>
          ))}
        </div>
      </components.MenuList>
    );
  };

  const getControlComponent = React.useCallback(
    ({ cx: selectCx, children, ...rest }: ControlProps<{}>) => (
      <components.Control cx={() => {}} {...rest}>
        <Button
          onClick={isEditable ? handleButtonClick : undefined}
          onKeyDown={handleTabNavigation}
          disabled={!isEditable}
          className={cx(
            "DatasheetsResultLookupTableField__button",
            {
              "DatasheetsResultLookupTableField__button--disabled": !isEditable,
              "DatasheetsResultLookupTableField__button--no-border": !showBorder,
            }
          )}
          style={{
            width: datasheetElement.width - 2,
            height: datasheetElement.height - 2,
            fontFamily: contentFontFamily,
            fontSize: contentFontSize,
            color: contentColor,
            textAlign: contentAlign,
            fontWeight: contentTextStyles.includes(DatasheetComponentTextStyle.bold) ? "bold" : undefined,
            fontStyle: contentTextStyles.includes(DatasheetComponentTextStyle.italic) ? "italic" : undefined,
            textDecoration: contentTextStyles.includes(DatasheetComponentTextStyle.underline) ? "underline" : undefined,
          }}
        >
          {forceDisabled ? '' : selectedItem?.value}
        </Button>
      </components.Control>
    ),
    [
      isEditable,
      forceDisabled,
      handleButtonClick,
      handleTabNavigation,
      showBorder,
      datasheetElement.width,
      datasheetElement.height,
      contentFontFamily,
      contentFontSize,
      contentColor,
      contentAlign,
      contentTextStyles,
      selectedItem,
    ],
  );

  return (
    <DatasheetItem
      style={{
        top: datasheetElement.coordinates.y,
        left: datasheetElement.coordinates.x,
      }}
    >
      {labelComponent}
      <Flex alignItems="center" style={{ position: 'relative' }}>
        <FieldContainer
          ref={containerRef}
          fieldId={datasheetElement.original.field.fieldid ?? datasheetElement.original.field.id}
          width={fieldWidth}
          height={fieldHeight}
          showBorder={datasheetElement.original.showborder}
          required={datasheetElement.original.required}
          isEmpty={fieldIsEmpty}
        >
          <ClickAwayListener onClickAway={handleCloseMenu}>
            <Dropdown
              fullWidth
              searchable={false}
              selectedValue={forceDisabled ? null : selectedItem?.value}
              options={normalizeDropdownOptions}
              menuIsOpen={isEditable ? menuOpened : false}
              placeholder={DROPDOWN_PLACEHOLDER}
              // menuListComponent={datasheetElement.original.field.options?.length ? getMenuListComponent : undefined}
              menuListComponent={getMenuListComponent}
              controlComponent={getControlComponent}
              styles={{
                width: "100%",
                menu: getMenuStyles,
                menuPortal: getMenuPortalStyles,
              }}
              onBlur={isEditable ? handleCloseMenu : undefined}
              onMenuClose={isEditable ? handleCloseMenu : undefined}
              disabled={!isEditable}
            />
          </ClickAwayListener>
        </FieldContainer>
        {datasheetElement.original.helptext && <Hint description={datasheetElement.original.helptext} />}
      </Flex>
    </DatasheetItem>
  );
});
